import React, {createContext, useContext, useMemo} from 'react';
import {DataCtx} from '../../dimijs/src/lib/utils/dataContext';

// Theme color definitions
export interface IThemeColors {
  // Primary action colors
  primary: string;
  primaryHover: string;
  primaryLight: string;
  primaryLighter: string;

  // Accent colors
  accent: string;
  accentHover: string;

  // Status colors
  success: string;
  danger: string;
  dangerLight: string;

  // Header/Navigation
  headerBg: string;
  headerText: string;
  navActive: string;
  navActiveText: string;

  // Badge colors
  badgeBg: string;
  badgeText: string;

  // Selected/Active states
  selectedBg: string;
  selectedBorder: string;
}

// Admin/Creator theme - Purple
const adminTheme: IThemeColors = {
  primary: '#9810fa',
  primaryHover: '#7a0dc8',
  primaryLight: '#f3e8ff',
  primaryLighter: '#faf5ff',

  accent: '#8200db',
  accentHover: '#6b00b5',

  success: '#10b981',
  danger: '#e7000b',
  dangerLight: '#fee2e2',

  headerBg: '#f0e9ff',
  headerText: '#101728',
  navActive: '#9810fa',
  navActiveText: '#ffffff',

  badgeBg: '#f3e8ff',
  badgeText: '#8200db',

  selectedBg: '#f3e8ff',
  selectedBorder: '#9810fa',
};

// User theme - Blue
const userTheme: IThemeColors = {
  primary: '#2563eb',
  primaryHover: '#1d4ed8',
  primaryLight: '#dbeafe',
  primaryLighter: '#eff6ff',

  accent: '#1e40af',
  accentHover: '#1e3a8a',

  success: '#10b981',
  danger: '#e7000b',
  dangerLight: '#fee2e2',

  headerBg: '#eff6ff',
  headerText: '#101728',
  navActive: '#2563eb',
  navActiveText: '#ffffff',

  badgeBg: '#dbeafe',
  badgeText: '#1e40af',

  selectedBg: '#dbeafe',
  selectedBorder: '#2563eb',
};

export type UserRole = 'admin' | 'creator' | 'moderator' | 'user' | 'guest';

export interface IThemeContext {
  theme: IThemeColors;
  role: UserRole;
  isAdmin: boolean;
  isCreator: boolean;
  isAdminOrCreator: boolean;
  canUploadTracks: boolean;
  canCreateDefaultPlaylists: boolean;
}

export const ThemeCtx = createContext<IThemeContext>({
  theme: adminTheme,
  role: 'guest',
  isAdmin: false,
  isCreator: false,
  isAdminOrCreator: false,
  canUploadTracks: false,
  canCreateDefaultPlaylists: false,
});

export const ThemeProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const dataContext = useContext(DataCtx);

  const value = useMemo<IThemeContext>(() => {
    const role = (dataContext.initProps?.role || 'guest') as UserRole;
    const isAdmin = role === 'admin';
    const isCreator = role === 'creator';
    const isAdminOrCreator = isAdmin || isCreator;

    // Admin and Creator use purple theme, everyone else uses blue
    const theme = isAdminOrCreator ? adminTheme : userTheme;

    return {
      theme,
      role,
      isAdmin,
      isCreator,
      isAdminOrCreator,
      canUploadTracks: isAdminOrCreator, // Only admin/creator can upload
      canCreateDefaultPlaylists: isAdminOrCreator, // Only admin/creator playlists are "default"
    };
  }, [dataContext.initProps?.role]);

  return (
    <ThemeCtx.Provider value={value}>
      {children}
    </ThemeCtx.Provider>
  );
};

// Hook for using theme
export const useTheme = () => {
  return useContext(ThemeCtx);
};

// CSS variable injection helper
export const injectThemeVariables = (theme: IThemeColors): React.CSSProperties => {
  return {
    '--theme-primary': theme.primary,
    '--theme-primary-hover': theme.primaryHover,
    '--theme-primary-light': theme.primaryLight,
    '--theme-primary-lighter': theme.primaryLighter,
    '--theme-accent': theme.accent,
    '--theme-accent-hover': theme.accentHover,
    '--theme-success': theme.success,
    '--theme-danger': theme.danger,
    '--theme-danger-light': theme.dangerLight,
    '--theme-header-bg': theme.headerBg,
    '--theme-header-text': theme.headerText,
    '--theme-nav-active': theme.navActive,
    '--theme-nav-active-text': theme.navActiveText,
    '--theme-badge-bg': theme.badgeBg,
    '--theme-badge-text': theme.badgeText,
    '--theme-selected-bg': theme.selectedBg,
    '--theme-selected-border': theme.selectedBorder,
  } as React.CSSProperties;
};
